'use client'

import classNames from 'classnames'
import { SourcingCompany } from '@productsite-beta/src/graphql/generated/graphql-types'
import type { ProductPageType } from '@productsite-beta/src/components/product-page-v2'
import {
  useSiteContext,
  useTranslations,
  useUserSettings,
} from '@dustin-web/microsite-components/src/hooks'
import { useI18n } from '@dustin-web/microsite-components/src/i18n/use-i18n'
import { Icon } from '@dustin-web/microsite-components/src/icon-library'
import Tooltip from '@dustin-web/microsite-components/src/tooltip'

export type Props = {
  product: ProductPageType
}

export type UspType = {
  text: string
  marketBlacklist: SourcingCompany[]
  tooltip?: string
}

export const ProductUsps = ({ product }: Props) => {
  const { translate } = useTranslations()
  const { sourcingCompany } = useSiteContext()
  const { showPricesInclVat } = useUserSettings()
  const { formatCurrency } = useI18n()

  const trustpilotRating = sourcingCompany === SourcingCompany.Se ? '4.5/5' : '4.3/5' //TODO: fetch data from Trustpilot

  const showRentalPrice =
    product.isRentable &&
    !!product.price?.rentalPrice?.amountExcludingVat &&
    !!product.price.rentalPrice.amountIncludingVat

  const rentalPrice =
    (showPricesInclVat
      ? product.price?.rentalPrice?.amountIncludingVat
      : product.price?.rentalPrice?.amountExcludingVat) || 0

  const uspArray: UspType[] = [
    {
      text: translate('USP_Fast_Deliveries'),
      marketBlacklist: [SourcingCompany.Dk],
    },
    {
      text: translate('USP_Free_Freight'),
      marketBlacklist: [
        SourcingCompany.Se,
        SourcingCompany.No,
        SourcingCompany.Dk,
        SourcingCompany.Fi,
      ],
    },
    {
      text: translate('USP_Easy_Returns_60days'),
      marketBlacklist: [SourcingCompany.No, SourcingCompany.Dk, SourcingCompany.Nl],
    },
    {
      text: translate('USP_Easy_Returns'),
      marketBlacklist: [SourcingCompany.Se, SourcingCompany.Fi, SourcingCompany.Nl],
    },
    {
      text: translate('USP_Customer_Rating', { rating: trustpilotRating }),
      marketBlacklist: [SourcingCompany.No, SourcingCompany.Dk, SourcingCompany.Nl],
    },
    {
      text: translate('USP_Secure_Payments'),
      marketBlacklist: [SourcingCompany.Se, SourcingCompany.Fi, SourcingCompany.Nl],
    },
  ]

  if (showRentalPrice) {
    uspArray.push({
      text: translate('Product_PayPerMonth_Description', {
        amount: formatCurrency(rentalPrice),
        months: product.price?.rentalPrice?.numberOfMonths,
      }),
      marketBlacklist: [SourcingCompany.Dk, SourcingCompany.Fi, SourcingCompany.Nl],
      tooltip: translate('USP_Rental_Tooltip'),
    })
  }

  const uspList = uspArray.filter((usp: any) => !usp.marketBlacklist.includes(sourcingCompany))

  return (
    <ul className="ul-stripped u-pt-24 u-m-0">
      {uspList.map((usp: UspType, i: number) => (
        <li
          key={i}
          className={classNames('d-flex align-center u-mb-8', {
            'u-m-0': i === uspList.length - 1,
          })}>
          <Icon name="check-large" className="u-fs-16" />
          <span className="u-fs-14 u-pl-8">{usp.text}</span>
          {usp.tooltip && (
            <Tooltip tooltipText={usp.tooltip}>
              <Icon name="info" className="i-16 u-pl-8" />
            </Tooltip>
          )}
        </li>
      ))}
    </ul>
  )
}

export default ProductUsps
